import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tag,
  Space,
  Spin,
  Grid,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  fetchSellers,
  createSeller,
  updateSeller,
  deleteSeller,
} from '../../store/slices/adminSlice';
import regionService from '../../services/regionService';
import ErrorMessage from '../common/ErrorMessage';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const SellerManager = () => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { sellers, loading, error } = useSelector((state) => state.admin);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [form] = Form.useForm();

  // Local state for regions
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);

  // Fetch regions on mount
  useEffect(() => {
    const loadRegions = async () => {
      setRegionsLoading(true);
      try {
        const response = await regionService.getAll();
        setRegions(response.regions || []);
      } catch (err) {
        console.error('Failed to fetch regions:', err);
      } finally {
        setRegionsLoading(false);
      }
    };
    loadRegions();
  }, []);

  useEffect(() => {
    dispatch(fetchSellers({ page, limit: pageSize }));
  }, [dispatch, page, pageSize]);

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleOpenDialog = (seller = null) => {
    if (seller) {
      setEditingSeller(seller);
      form.setFieldsValue({
        telegram_id: seller.telegram_id || '',
        region_id: seller.region_id || undefined,
      });
    } else {
      setEditingSeller(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSeller(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    if (editingSeller) {
      await dispatch(
        updateSeller({
          id: editingSeller.id,
          data: { region_id: values.region_id },
        })
      );
    } else {
      await dispatch(createSeller(values));
    }
    handleCloseDialog();
    dispatch(fetchSellers({ page, limit: pageSize }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Haqiqatan ham ushbu sotuvchini o\'chirmoqchimisiz?')) {
      await dispatch(deleteSeller(id));
      dispatch(fetchSellers({ page, limit: pageSize }));
    }
  };

  if (loading && !sellers.list.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Yuklanmoqda..." />
      </div>
    );
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Nomi',
      key: 'name',
      render: (_, record) => (
        <Text strong>{record.first_name} {record.last_name}</Text>
      ),
    },
    {
      title: 'Telegram ID',
      dataIndex: 'telegram_id',
      key: 'telegram_id',
      responsive: ['md'],
    },
    {
      title: 'Telefon',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone) => phone || 'Mavjud emas',
      responsive: ['sm'],
    },
    {
      title: 'Region',
      key: 'region',
      render: (_, record) => record.Region ? record.Region.name : 'Biriktirilmagan',
      responsive: ['sm'],
    },
    {
      title: 'Holati',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.is_active ? 'success' : 'default'}>
          {record.is_active ? 'Faol' : 'Faol emas'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenDialog(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
      align: 'right',
      width: 100,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'stretch' : 'center',
          gap: 16,
          marginBottom: 24
        }}
      >
        <Title level={2} style={{ margin: 0 }}>Sotuvchilarni boshqarish</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenDialog()}
          block={screens.xs}
        >
          Sotuvchi qo&apos;shish
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <Table
        columns={columns}
        dataSource={sellers.list}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: sellers.pagination.total,
          showSizeChanger: true,
          nextIcon: 'Keyingi',
          prevIcon: 'Oldingi',
        }}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
      />

      {/* Add/Edit Dialog */}
      <Modal
        title={editingSeller ? 'Sotuvchini tahrirlash' : 'Yangi sotuvchi qo\'shish'}
        open={openDialog}
        onCancel={handleCloseDialog}
        onOk={() => form.submit()}
        okText={editingSeller ? 'Yangilash' : 'Yaratish'}
        cancelText="Bekor qilish"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {!editingSeller && (
            <Form.Item
              name="telegram_id"
              label="Telegram ID"
              rules={[{ required: true, message: 'Iltimos Telegram ID kiriting' }]}
              help="Sotuvchiga aylantirish uchun foydalanuvchining Telegram ID sini kiriting"
            >
              <Input />
            </Form.Item>
          )}

          <Form.Item
            name="region_id"
            label="Region"
            rules={[{ required: true, message: 'Iltimos regionni tanlang' }]}
          >
            <Select placeholder="Regionni tanlang">
              {regions.map((region) => (
                <Option key={region.id} value={region.id}>
                  {region.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SellerManager;
